<dialog-header
  title="Voucher Site Configuration"
  (onClose)="close()"
></dialog-header>

<div mat-dialog-content class="content-container">
  <form [formGroup]="form" class="config-form">
    <!-- Status Section -->
    <div class="status-section">
      <div class="status-item">
        <span class="status-label">Status:</span>
        <span class="status-value" [style.color]="statusColor">{{
          approvalStatus
        }}</span>
      </div>

      <div class="status-item" *ngIf="micrositeUrl">
        <span class="status-label">Site URL:</span>
        <div class="url-container">
          <span class="url-text">{{ micrositeUrl }}</span>
          <div class="url-actions">
            <button
              type="button"
              mat-icon-button
              (click)="copyToClipboard()"
              matTooltip="Copy to clipboard"
            >
              <mat-icon>content_copy</mat-icon>
            </button>
            <button
              type="button"
              mat-icon-button
              (click)="openInNewTab()"
              matTooltip="Open in new tab"
            >
              <mat-icon>open_in_new</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Enable Toggle -->
    <div class="form-section">
      <mat-slide-toggle formControlName="enabled">
        Enable Microsite
      </mat-slide-toggle>
      <p class="help-text">
        Enable this to make your vouchers available on a public microsite. Your
        configuration will be submitted for approval.
      </p>
    </div>

    <!-- Branding -->
    <div class="form-section">
      <h3>Branding</h3>

      <div class="form-field">
        <div class="form-field-label">
          Subdomain<span style="color: red">*</span>
        </div>
        <div class="subdomain-input-container">
          <input
            class="form-input subdomain-input"
            placeholder="e.g. mystore"
            autocomplete="off"
            formControlName="subdomain"
            type="text"
            [ngStyle]="{
              border:
                form.get('subdomain')?.invalid && form.get('subdomain')?.touched
                  ? '1px solid red'
                  : '1px solid var(--app-light-grey)',
            }"
          />
          <span class="subdomain-suffix">.remyrewards.co.uk</span>
        </div>
        <div class="help-text" *ngIf="!isApproved">
          Choose a unique subdomain (lowercase letters only). This cannot be
          changed once approved.
        </div>
        <div class="help-text" style="color: orange" *ngIf="isApproved">
          Subdomain is locked after approval
        </div>
        <div
          class="error-text"
          *ngIf="
            form.get('subdomain')?.invalid && form.get('subdomain')?.touched
          "
          style="color: red; font-size: 12px; margin-top: 4px"
        >
          <span *ngIf="form.get('subdomain')?.hasError('required')"
            >Subdomain is required</span
          >
          <span *ngIf="form.get('subdomain')?.hasError('pattern')"
            >Only lowercase letters are allowed</span
          >
          <span *ngIf="form.get('subdomain')?.hasError('minlength')"
            >Subdomain must be at least 3 characters</span
          >
          <span *ngIf="form.get('subdomain')?.hasError('maxlength')"
            >Subdomain must be no more than 20 characters</span
          >
        </div>
      </div>

      <div class="form-field">
        <div class="form-field-label">
          Page Title<span style="color: red">*</span>
        </div>
        <input
          class="form-input"
          placeholder="e.g. Vouchers"
          autocomplete="off"
          formControlName="title"
          type="text"
        />
      </div>

      <div class="form-field">
        <div class="form-field-label">Google Analytics Tracking ID</div>
        <input
          class="form-input"
          placeholder="e.g. G-XXXXXXXXXX or UA-XXXXXXXXX-X"
          autocomplete="off"
          formControlName="googleAnalyticsId"
          type="text"
        />
        <p class="help-text">
          Optional: Add your Google Analytics tracking ID to monitor microsite
          traffic
        </p>
      </div>

      <div class="upload-section">
        <div class="upload-item">
          <label>Logo</label>
          <p class="help-text">
            Displayed in the header of your microsite (1:1 aspect ratio)
          </p>
          <file-upload
            [aspectRatio]="1 / 1"
            (fileSelected)="onLogoSelected($event)"
            [maxFileSize]="1048576"
            [readerResult]="currentLogoUrl ?? ''"
          ></file-upload>
        </div>

        <div class="upload-item">
          <label>Cover Image</label>
          <p class="help-text">
            Hero image displayed on your microsite (16:9 aspect ratio)
          </p>
          <file-upload
            [aspectRatio]="16 / 9"
            (fileSelected)="onCoverSelected($event)"
            [maxFileSize]="2097152"
            [readerResult]="currentCoverUrl ?? ''"
          ></file-upload>
        </div>

        <div class="upload-item">
          <label>Email Artwork</label>
          <p class="help-text">
            Optional artwork for voucher PDFs (1600x466 aspect ratio)
            <span
              ><a href="assets/cover-artwork-voucher.pdf" target="_blank"
                >PDF Voucher Example</a
              ></span
            >
          </p>
          <file-upload
            [aspectRatio]="1600 / 466"
            (fileSelected)="onEmailArtworkSelected($event)"
            [maxFileSize]="2097152"
            [readerResult]="currentEmailArtworkUrl ?? ''"
          ></file-upload>
        </div>
      </div>

      <div class="color-section">
        <h4>Colors</h4>
        <div class="color-grid">
          <!-- <div class="color-item">
            <label>Primary Color</label>
            <input
              type="color"
              formControlName="primaryColor"
              class="color-input"
            />
          </div> -->

          <!-- <div class="color-item">
            <label>Secondary Color</label>
            <input
              type="color"
              formControlName="secondaryColor"
              class="color-input"
            />
          </div>

          <div class="color-item">
            <label>Tertiary Color</label>
            <input
              type="color"
              formControlName="tertiaryColor"
              class="color-input"
            />
          </div> -->

          <div class="color-item">
            <label>Background</label>
            <input
              type="color"
              formControlName="backgroundColor"
              class="color-input"
            />
          </div>

          <div class="color-item">
            <label>Text</label>
            <input
              type="color"
              formControlName="textColor"
              class="color-input"
            />
          </div>

          <div class="color-item">
            <label>Button</label>
            <input
              type="color"
              formControlName="buttonColor"
              class="color-input"
            />
          </div>

          <div class="color-item">
            <label>Button Text</label>
            <input
              type="color"
              formControlName="buttonTextColor"
              class="color-input"
            />
          </div>

          <div class="color-item">
            <label>Discount Badge</label>
            <input
              type="color"
              formControlName="discountBadgeColor"
              class="color-input"
            />
          </div>

          <div class="color-item">
            <label>Discount Badge Text</label>
            <input
              type="color"
              formControlName="discountBadgeTextColor"
              class="color-input"
            />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>Styling</h3>
        <p class="help-text">
          Choose the style of edges for buttons and components
        </p>

        <div class="edge-preview">
          <div
            class="edge-example square"
            [class.selected]="form.get('edgeType')?.value === 'square'"
            (click)="form.get('edgeType')?.setValue('square')"
          >
            <div class="edge-label">Square</div>
          </div>
          <div
            class="edge-example rounded"
            [class.selected]="form.get('edgeType')?.value === 'rounded'"
            (click)="form.get('edgeType')?.setValue('rounded')"
          >
            <div class="edge-label">Rounded</div>
          </div>
        </div>
      </div>

      <!-- <div class="form-section">
        <h3>Typography</h3>
        <p class="help-text">Choose a font for your microsite</p> -->

      <!-- <div class="form-field">
          <single-select
            label="Font"
            [formCtrl]="fontFormControl"
            [options]="fontOptions"
            property="label"
          ></single-select>
        </div>

        <div class="font-preview" *ngIf="fontFormControl?.value?.value">
          <div
            class="font-preview-text"
            [style.fontFamily]="fontFormControl.value?.value"
          >
            <div class="font-preview-heading">
              The quick brown fox jumps over the lazy dog
            </div>
            <div class="font-preview-body">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </div>
          </div>
        </div> -->
      <!-- </div> -->
    </div>

    <!-- Contact Information -->
    <div class="form-section">
      <h3>Contact Information</h3>

      <div class="form-field">
        <div class="form-field-label">Phone Number</div>
        <input
          class="form-input"
          placeholder="Enter phone number"
          autocomplete="off"
          formControlName="phone"
          type="tel"
        />
      </div>

      <div class="form-field">
        <div class="form-field-label">Email Address</div>
        <input
          class="form-input"
          placeholder="Enter email address"
          autocomplete="off"
          formControlName="email"
          type="email"
        />
        <mat-error *ngIf="form.get('email')?.hasError('email')">
          <span style="font-size: 12px"
            >Please enter a valid email address</span
          >
        </mat-error>
      </div>

      <div class="form-field">
        <div class="form-field-label">
          Website<span style="color: red">*</span>
        </div>
        <input
          class="form-input"
          placeholder="Enter website URL"
          autocomplete="off"
          formControlName="website"
        />
        <mat-error *ngIf="form.get('email')?.hasError('email')">
          <span style="font-size: 12px">Please enter a valid website URL</span>
        </mat-error>
      </div>

      <div class="form-field">
        <div class="form-field-label">Address Line 1</div>
        <input
          class="form-input"
          placeholder="Enter address line 1"
          autocomplete="off"
          formControlName="addressLine1"
          type="text"
        />
      </div>

      <div class="form-field">
        <div class="form-field-label">Address Line 2</div>
        <input
          class="form-input"
          placeholder="Enter address line 2"
          autocomplete="off"
          formControlName="addressLine2"
          type="text"
        />
      </div>

      <div class="form-field">
        <div class="form-field-label">City</div>
        <input
          class="form-input"
          placeholder="Enter city"
          autocomplete="off"
          formControlName="addressCity"
          type="text"
        />
      </div>

      <div class="form-field">
        <div class="form-field-label">Postal Code</div>
        <input
          class="form-input"
          placeholder="Enter postal code"
          autocomplete="off"
          formControlName="addressPostalCode"
          type="text"
        />
      </div>

      <div class="form-field">
        <single-select
          label="Country"
          *ngIf="countries?.length"
          [formCtrl]="countryFormControl"
          [options]="countries"
          property="name"
          [value]="countryFormControl.value"
          [showSearch]="true"
        ></single-select>
      </div>
    </div>
  </form>
</div>

<div mat-dialog-actions class="actions-container">
  <div class="left-actions">
    <!-- Empty for now -->
  </div>

  <div class="right-actions">
    <app-primary-button
      [title]="'Cancel'"
      (tapped)="close()"
      bgColor="transparent"
      color="var(--app-text)"
    ></app-primary-button>

    <app-primary-button
      [title]="'Save'"
      [disabled]="form.invalid || saving"
      (tapped)="save()"
      bgColor="var(--app-timber)"
      color="white"
    ></app-primary-button>
  </div>
</div>

<loader [loading]="saving"></loader>
